export const DEFAULT_GOLF_TIME_ZONE = 'Europe/London'
const DATE_KEY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/

export function resolveCourseTimeZone(course) {
  return course?.time_zone || course?.timeZone || DEFAULT_GOLF_TIME_ZONE
}

export function parseDateKey(dateKey) {
  const match = DATE_KEY_REGEX.exec(String(dateKey || ''))
  if (!match) return null

  const [, yStr, mStr, dStr] = match
  const year = Number.parseInt(yStr, 10)
  const month = Number.parseInt(mStr, 10)
  const day = Number.parseInt(dStr, 10)

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return null
  }

  return date
}

export function formatDateKey(date) {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function normalizeDateKey(dateKey) {
  const parsed = parseDateKey(dateKey)
  return parsed ? formatDateKey(parsed) : null
}

export function addDaysToDateKey(dateKey, days) {
  const base = parseDateKey(dateKey)
  if (!base) return null
  const copy = new Date(base.getTime())
  copy.setUTCDate(copy.getUTCDate() + days)
  return formatDateKey(copy)
}

export function compareDateKeys(a, b) {
  const first = normalizeDateKey(a)
  const second = normalizeDateKey(b)
  if (!first || !second) return null
  if (first === second) return 0
  return first < second ? -1 : 1
}

export function clampDateKey(dateKey, minDateKey, maxDateKey = null) {
  const normalized = normalizeDateKey(dateKey)
  if (!normalized) return null

  const minNormalized = normalizeDateKey(minDateKey)
  const maxNormalized = normalizeDateKey(maxDateKey)

  if (minNormalized && compareDateKeys(normalized, minNormalized) < 0) {
    return minNormalized
  }

  if (maxNormalized && compareDateKeys(normalized, maxNormalized) > 0) {
    return maxNormalized
  }

  return normalized
}

export function normalizeBookingWindow(value) {
  const parsed = Number.parseInt(value, 10)
  if (!Number.isInteger(parsed) || parsed < 0) return null
  return parsed
}

export function getBookingWindowLimits(bookingWindow, timeZone = DEFAULT_GOLF_TIME_ZONE) {
  const normalizedWindow = normalizeBookingWindow(bookingWindow)
  const todayKey = getTodayDateKey(timeZone)
  const maxDateKey =
    Number.isInteger(normalizedWindow) && normalizedWindow >= 0 && todayKey
      ? addDaysToDateKey(todayKey, normalizedWindow)
      : null

  return { todayKey, maxDateKey, bookingWindow: normalizedWindow }
}

export function buildDateKeySequence(startDateKey, count) {
  const start = parseDateKey(startDateKey)
  if (!start || !Number.isFinite(count) || count < 1) return []

  const keys = []
  for (let i = 0; i < count; i += 1) {
    const next = new Date(start.getTime())
    next.setUTCDate(start.getUTCDate() + i)
    keys.push(formatDateKey(next))
  }

  return keys
}

function formatDateKeyWithOptions(dateKey, timeZone, options, locale) {
  const parsed = parseDateKey(dateKey)
  if (!parsed) return null
  return parsed.toLocaleDateString(locale, { ...options, timeZone: 'UTC' })
}

export function formatWeekdayShort(dateKey, timeZone, locale) {
  return formatDateKeyWithOptions(dateKey, timeZone, { weekday: 'short' }, locale)
}

export function formatMonthDayShort(dateKey, timeZone, locale) {
  return formatDateKeyWithOptions(dateKey, timeZone, { month: 'short', day: 'numeric' }, locale)
}

export function formatMonthShort(dateKey, timeZone, locale) {
  return formatDateKeyWithOptions(dateKey, timeZone, { month: 'short' }, locale)
}

export function formatLongDate(dateKey, timeZone, locale) {
  return formatDateKeyWithOptions(dateKey, timeZone, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }, locale)
}

export function getDateKeyForInstant(date, timeZone = DEFAULT_GOLF_TIME_ZONE) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const parts = formatter.formatToParts(date)
  const lookup = Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]))

  const y = lookup.year
  const m = lookup.month
  const d = lookup.day

  if (!y || !m || !d) return null
  return `${y}-${m}-${d}`
}

export function getTodayDateKey(timeZone = DEFAULT_GOLF_TIME_ZONE) {
  return getDateKeyForInstant(new Date(), timeZone)
}

function getZonedTimeParts(value, timeZone = DEFAULT_GOLF_TIME_ZONE) {
  const date = value instanceof Date ? value : new Date(value)
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  })

  const parts = formatter.formatToParts(date)
  const lookup = Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]))

  const hour = Number.parseInt(lookup.hour, 10)
  const minute = Number.parseInt(lookup.minute, 10)

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null
  return { hour, minute }
}

export function formatTimeInTimeZone(value, timeZone = DEFAULT_GOLF_TIME_ZONE, locale) {
  const date = value instanceof Date ? value : new Date(value)
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '--:--'

  return date.toLocaleTimeString(locale, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function getZonedMinutes(value, timeZone = DEFAULT_GOLF_TIME_ZONE) {
  const parts = getZonedTimeParts(value, timeZone)
  if (!parts) return null
  return parts.hour * 60 + parts.minute
}
